import { useContext } from 'react';
import { useNavigate } from 'react-router';

import { Box, Button, Stack, Typography } from '@mui/material';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext } from 'src/AnalyticsContext';
import PendantButtonIcon from 'src/assets/images/device-icons/pendant-button-icon.svg?react';
import LastPressTimeDisplay from 'src/components/fragments/LastPressTimeDisplay';
import LowBatteryDisplay from 'src/components/fragments/LowBatteryDisplay';
import useDeviceBatteryLevels from 'src/hooks/useDeviceBatteryLevels';
import useUserPermissions from 'src/hooks/useUserPermissions';
import { Device, DeviceAllocation } from 'src/store/sageAdminApi';
import { getDevicesFromAllocations } from 'src/utils/getDevicesFromAllocation';

const DrawerName = 'Resident Pendants Drawer';

export interface ResidentPendantsDrawerProps extends DrawerComponentProps {
    pendantAllocations: DeviceAllocation[];
}

function ResidentPendantsDrawer(props: ResidentPendantsDrawerProps) {
    const { onClose, title = '', pendantAllocations } = props;
    const { isInManagementGroup } = useUserPermissions();
    const { trackEvent } = useContext(AnalyticsContext);
    const navigate = useNavigate();

    const { deviceBatteryLevelsMap } = useDeviceBatteryLevels({
        deviceAllocations: pendantAllocations,
    });

    const { devicesWithAllocations: pendantDevices } =
        getDevicesFromAllocations(pendantAllocations);

    const handleCancel = () => {
        trackEvent('Cancel Button Clicked', { context: DrawerName });
        onClose();
    };

    const handlePendantClick = (device: Device) => {
        trackEvent('Resident Pendant Clicked', {
            context: DrawerName,
            deviceId: device.id,
        });
        if (isInManagementGroup) {
            onClose();
            navigate(`/devices/${device?.id}`);
        }
    };

    return (
        <>
            <DrawerTitle title={title} />
            <DrawerBody sx={{ gap: 2, pb: 2 }}>
                {pendantDevices.map(({ device, allocation }, index) => (
                    <Box
                        key={allocation.id || index}
                        onClick={() => {
                            handlePendantClick(device);
                        }}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                            boxShadow: 1,
                            borderRadius: 1,
                            cursor: isInManagementGroup ? 'pointer' : 'default',
                        }}
                    >
                        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                            <PendantButtonIcon />
                            <Stack>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 'bold', color: 'grey.900' }}
                                >
                                    Resident Pendant
                                </Typography>
                                {device?.id && deviceBatteryLevelsMap.has(device.id) && (
                                    <LowBatteryDisplay
                                        batteryLevel={deviceBatteryLevelsMap.get(device.id)!}
                                    />
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                            {device?.lastPressTimestamp && (
                                <LastPressTimeDisplay
                                    lastPressTimestamp={Number(device.lastPressTimestamp)}
                                />
                            )}
                        </Stack>
                    </Box>
                ))}
            </DrawerBody>
            <DrawerFooter>
                <Button
                    size="large"
                    color="primary"
                    disableElevation
                    fullWidth
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
            </DrawerFooter>
        </>
    );
}

export default ResidentPendantsDrawer;
